/**
 * Anagrafica fornitori e livello di riservatezza.
 *
 * È l'**unico** punto del sistema da cui si cambia `phc_livelloriservatezza` (ADR-0002), e la
 * pagina è costruita attorno alla differenza fra i quattro livelli, che è facile confondere:
 *
 * - `Riservato` / `Strettamente riservato` **nascondono** il fornitore a manutenzioni, e per
 *   questo la route rifiuta il passaggio se il fornitore ha ancora record di là (invariante L1).
 *   L'impronta si chiede *prima* di proporre il cambio, così il rifiuto non arriva a sorpresa.
 * - `In trattativa` **non nasconde: maschera**. Il nome diventa «Fornitore riservato» ma scadenze
 *   e interventi restano visibili e operativi. Non ha invariante, perché è per costruzione il
 *   livello di chi ha una storia in manutenzioni ed è in gara.
 *
 * `In trattativa` sale da solo (all'invio di una gara, sprint 04) ma **scende solo da qui**: il
 * promemoria periodico esiste proprio perché nessuno se ne dimentichi.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@shared/context/AuthContext";
import { useToast } from "@shared/components/ui/Toast";
import { fornitoriApi } from "../services/acquistiApi";
import {
  Campo,
  Testo,
  Scelta,
  Caricamento,
  Vuoto,
  Avviso,
} from "../components/campi";
import {
  LIVELLO_RISERVATEZZA,
  LIVELLO_BADGE,
  LIVELLO_IN_TRATTATIVA,
  opzioni,
} from "../constants/optionSet";
import { dataIt, giorniA, etichetta } from "../utils/formato";

const LIVELLI_CHE_NASCONDONO = [100000002, 100000003];

/** Da quanti giorni «In trattativa» il promemoria comincia a segnalare il fornitore. */
const SOGLIA_PROMEMORIA = 30;

function PannelloLivello({ fornitore, onChiudi, onFatto }) {
  const { showToast } = useToast();
  const [livello, setLivello] = useState(String(fornitore.livello));
  const [impronta, setImpronta] = useState(null);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState(null);

  useEffect(() => {
    fornitoriApi
      .impronta(fornitore.id)
      .then(setImpronta)
      .catch(() => setImpronta(null));
  }, [fornitore.id]);

  const nasconde = LIVELLI_CHE_NASCONDONO.includes(Number(livello));
  const bloccato = nasconde && impronta && !impronta.puoEssereNascosto;

  const salva = async () => {
    setInCorso(true);
    setErrore(null);
    try {
      await fornitoriApi.cambiaLivello(fornitore.id, Number(livello));
      showToast("Livello aggiornato", "success");
      onFatto();
    } catch (e) {
      setErrore(e.data?.message || e.message || "Cambio livello rifiutato");
    } finally {
      setInCorso(false);
    }
  };

  return (
    <div className="rounded-md border border-acquisti-200 bg-acquisti-50/40 p-4 space-y-3">
      <div className="flex flex-wrap gap-4 items-end">
        <Campo label="Livello di riservatezza" className="min-w-64">
          <Scelta
            valore={livello}
            onChange={setLivello}
            opzioni={opzioni(LIVELLO_RISERVATEZZA)}
            vuoto="—"
          />
        </Campo>
        <div className="flex gap-2 pb-1">
          <button type="button" className="btn-secondary" onClick={onChiudi}>
            Annulla
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={salva}
            disabled={
              inCorso || bloccato || Number(livello) === fornitore.livello
            }
          >
            {inCorso ? "Salvataggio…" : "Applica"}
          </button>
        </div>
      </div>

      {Number(livello) === LIVELLO_IN_TRATTATIVA && (
        <Avviso tipo="attenzione">
          «In trattativa» <strong>maschera</strong> il nome nel portale
          manutenzioni ma lascia visibili e operativi scadenze e interventi. È
          temporaneo e <strong>scende solo a mano</strong>: dopo{" "}
          {SOGLIA_PROMEMORIA} giorni il sistema comincia a ricordarvelo via
          email.
        </Avviso>
      )}

      {nasconde && (
        <>
          {impronta === null ? (
            <p className="text-sm text-gray-500">Verifica dell'impronta…</p>
          ) : impronta.puoEssereNascosto ? (
            <Avviso tipo="ok">
              Nessun record del fornitore nel portale manutenzioni: può essere
              nascosto.
            </Avviso>
          ) : (
            <Avviso tipo="errore">
              <strong>Non si può nascondere</strong>: il fornitore ha{" "}
              {impronta.totale} record nel portale manutenzioni.
              <ul className="list-disc ml-5 mt-1">
                {Object.entries(impronta.dettaglio || {})
                  .filter(([, n]) => n)
                  .map(([k, n]) => (
                    <li key={k}>
                      {k}: {String(n)}
                    </li>
                  ))}
              </ul>
              <div className="mt-1">
                Per una trattativa in corso il livello giusto è{" "}
                <strong>In trattativa</strong>, che ne maschera il nome senza
                spegnere niente.
              </div>
            </Avviso>
          )}
        </>
      )}

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </div>
  );
}

/**
 * PEC, sede legale, P.IVA e C.F.: i dati che la lettera di chiusura del contratto cita. Si
 * correggono da qui perche' l'Ufficio Acquisti non passa dal portale manutenzioni, e una PEC
 * sbagliata e' una disdetta che non arriva.
 */
function PannelloAnagrafica({ fornitore, onChiudi, onFatto }) {
  const { showToast } = useToast();
  const [form, setForm] = useState({
    pec: fornitore.pec ?? "",
    partitaIva: fornitore.partitaIva ?? "",
    codiceFiscale: fornitore.codiceFiscale ?? "",
    indirizzoSedeLegale: fornitore.indirizzoSedeLegale ?? "",
    capSedeLegale: fornitore.capSedeLegale ?? "",
    comuneSedeLegale: fornitore.comuneSedeLegale ?? "",
    provinciaSedeLegale: fornitore.provinciaSedeLegale ?? "",
  });
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const salva = async () => {
    setInCorso(true);
    setErrore(null);
    try {
      await fornitoriApi.aggiornaAnagrafica(fornitore.id, form);
      showToast("Anagrafica aggiornata", "success");
      onFatto();
    } catch (e) {
      setErrore(e.data?.message || e.message || "Salvataggio rifiutato");
    } finally {
      setInCorso(false);
    }
  };

  return (
    <div className="rounded-md border border-acquisti-200 bg-acquisti-50/40 p-4 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Campo
          label="PEC"
          aiuto="Verificarla sull'ultimo documento ricevuto o su INI-PEC."
        >
          <Testo valore={form.pec} onChange={set("pec")} type="email" />
        </Campo>
        <Campo label="Partita IVA (11 cifre)">
          <Testo
            valore={form.partitaIva}
            onChange={set("partitaIva")}
            maxLength={11}
          />
        </Campo>
        <Campo label="Sede legale — via e numero" className="md:col-span-2">
          <Testo
            valore={form.indirizzoSedeLegale}
            onChange={set("indirizzoSedeLegale")}
            placeholder="Via …, 14"
            maxLength={200}
          />
        </Campo>
        <div className="grid grid-cols-3 gap-3 md:col-span-2">
          <Campo label="CAP">
            <Testo
              valore={form.capSedeLegale}
              onChange={set("capSedeLegale")}
              maxLength={5}
            />
          </Campo>
          <Campo label="Comune">
            <Testo
              valore={form.comuneSedeLegale}
              onChange={set("comuneSedeLegale")}
              maxLength={100}
            />
          </Campo>
          <Campo label="Prov.">
            <Testo
              valore={form.provinciaSedeLegale}
              onChange={(v) => set("provinciaSedeLegale")(v.toUpperCase())}
              maxLength={2}
              placeholder="TV"
            />
          </Campo>
        </div>
        <Campo label="Codice fiscale">
          <Testo
            valore={form.codiceFiscale}
            onChange={set("codiceFiscale")}
            maxLength={16}
          />
        </Campo>
      </div>
      <div className="flex gap-2">
        <button type="button" className="btn-secondary" onClick={onChiudi}>
          Annulla
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={salva}
          disabled={inCorso}
        >
          {inCorso ? "Salvataggio…" : "Salva anagrafica"}
        </button>
      </div>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </div>
  );
}

export default function FornitoriPage() {
  const { user } = useAuth();
  const puoCambiare = [100000000, 100000007].includes(user?.ruolo);
  const [anagraficaAperta, setAnagraficaAperta] = useState(null);

  const [filtri, setFiltri] = useState({ search: "", livello: "" });
  const [fornitori, setFornitori] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState(null);
  const [aperto, setAperto] = useState(null);

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErrore(null);
    try {
      const res = await fornitoriApi.list(filtri);
      setFornitori(res.value || []);
    } catch (e) {
      setErrore(e.message || "Impossibile caricare i fornitori");
    } finally {
      setCaricamento(false);
    }
  }, [filtri]);

  useEffect(() => {
    const t = setTimeout(carica, 300);
    return () => clearTimeout(t);
  }, [carica]);

  const set = (k) => (v) => setFiltri((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900">Fornitori</h1>
        <span className="text-sm text-gray-500">
          {caricamento ? "…" : `${fornitori.length} fornitori`}
        </span>
      </div>

      <div className="card !p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Campo label="Cerca">
          <Testo
            valore={filtri.search}
            onChange={set("search")}
            placeholder="ragione sociale"
          />
        </Campo>
        <Campo label="Livello">
          <Scelta
            valore={filtri.livello}
            onChange={set("livello")}
            opzioni={opzioni(LIVELLO_RISERVATEZZA)}
            vuoto="tutti"
          />
        </Campo>
      </div>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      <div className="card !p-0 overflow-x-auto">
        {caricamento ? (
          <Caricamento />
        ) : fornitori.length === 0 ? (
          <Vuoto titolo="Nessun fornitore" />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Fornitore</th>
                <th>P. IVA</th>
                <th>PEC</th>
                <th>Livello</th>
                <th>Dal</th>
                <th>Portale</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {fornitori.map((f) => {
                const inTrattativaDa =
                  f.livello === LIVELLO_IN_TRATTATIVA
                    ? -(giorniA(f.dataLivello) ?? 0)
                    : null;
                return (
                  <React.Fragment key={f.id}>
                    <tr>
                      <td className="font-medium text-gray-900">{f.nome}</td>
                      <td>{f.partitaIva || "—"}</td>
                      <td className="text-xs">
                        {f.pec || (
                          <span
                            className="text-yellow-800"
                            title="Senza PEC non si può produrre la disdetta"
                          >
                            mancante
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          className={LIVELLO_BADGE[f.livello] || "badge-gray"}
                        >
                          {f.livelloLabel ||
                            etichetta(LIVELLO_RISERVATEZZA, f.livello)}
                        </span>
                        {inTrattativaDa != null &&
                          inTrattativaDa >= SOGLIA_PROMEMORIA && (
                            <span
                              className="block text-xs text-yellow-800 mt-0.5"
                              title="Oltre la soglia del promemoria periodico"
                            >
                              mascherato da {inTrattativaDa} giorni
                            </span>
                          )}
                      </td>
                      <td className="whitespace-nowrap">
                        {f.dataLivello ? dataIt(f.dataLivello) : "—"}
                      </td>
                      <td>
                        {f.abilitatoPortale ? (
                          <span className="badge-info">abilitato</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="text-right whitespace-nowrap">
                        {puoCambiare && (
                          <>
                            <button
                              type="button"
                              className="text-acquisti-700 hover:underline text-sm mr-3"
                              onClick={() =>
                                setAnagraficaAperta(
                                  anagraficaAperta === f.id ? null : f.id,
                                )
                              }
                            >
                              {anagraficaAperta === f.id
                                ? "Chiudi"
                                : "Anagrafica"}
                            </button>
                            <button
                              type="button"
                              className="text-acquisti-700 hover:underline text-sm"
                              onClick={() =>
                                setAperto(aperto === f.id ? null : f.id)
                              }
                            >
                              {aperto === f.id ? "Chiudi" : "Cambia livello"}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {anagraficaAperta === f.id && (
                      <tr>
                        <td colSpan={7} className="!p-3 bg-gray-50">
                          <PannelloAnagrafica
                            fornitore={f}
                            onChiudi={() => setAnagraficaAperta(null)}
                            onFatto={() => {
                              setAnagraficaAperta(null);
                              carica();
                            }}
                          />
                        </td>
                      </tr>
                    )}
                    {aperto === f.id && (
                      <tr>
                        <td colSpan={7} className="!p-3 bg-gray-50">
                          <PannelloLivello
                            fornitore={f}
                            onChiudi={() => setAperto(null)}
                            onFatto={() => {
                              setAperto(null);
                              carica();
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
